import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { PasswordInput } from './ui/password-input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Lock, AlertCircle } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForcePasswordChangeModal({ isOpen, onClose }) {
  const { user, token, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    if (newPassword === currentPassword) {
      toast.error('New password must be different from current password');
      return;
    }

    setLoading(true);

    try {
      await axios.post(
        `${API_URL}/auth/change-password`,
        {
          current_password: currentPassword,
          new_password: newPassword
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Password changed successfully! Please log in again with your new password.');
      
      // Log out the user and redirect to login page
      setTimeout(() => {
        logout();
        window.location.href = '/login';
      }, 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md" data-testid="force-password-modal">
        <DialogHeader className="text-center space-y-3">
          <div className="mx-auto w-14 h-14 bg-amber-500 rounded-2xl flex items-center justify-center shadow-md">
            <Lock className="w-7 h-7 text-white" />
          </div>
          <DialogTitle className="text-2xl font-heading" data-testid="modal-title">
            Password Change Required
          </DialogTitle>
          <DialogDescription asChild data-testid="modal-description">
            <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-3 rounded-lg mt-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm text-left">
                You are using a temporary password. Please change it to a secure password before continuing.
              </span>
            </div>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleChangePassword} className="space-y-4 mt-4" data-testid="password-change-form">
          <div className="space-y-2">
            <Label htmlFor="current_password">Temporary Password</Label>
            <PasswordInput
              id="current_password"
              placeholder="Enter your temporary password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              className="h-11"
              data-testid="temp-password-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new_password">New Password</Label>
            <PasswordInput
              id="new_password"
              placeholder="Enter new password (min 6 characters)"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={6}
              className="h-11"
              data-testid="new-password-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm_password">Confirm New Password</Label>
            <PasswordInput
              id="confirm_password"
              placeholder="Re-enter new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              className="h-11"
              data-testid="confirm-password-input"
            />
          </div>
          <DialogFooter className="mt-6">
            <Button
              type="submit"
              className="w-full h-11 rounded-full text-base font-medium"
              disabled={loading}
              data-testid="submit-password-change"
            >
              {loading ? 'Changing Password...' : 'Change Password & Re-Login'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
